// frontend/src/components/payroll/PayStub/styles.ts
// Palette constants + the full styles object used by PayStub's layout.

import type { CSSProperties } from "react";

// ── Palette ─────────────────────────────────────────────────────────────
export const TEAL = "#15A08C";
export const PAPER = "#FFFFFF";
export const INK = "#1A1A1A";
export const MUTED = "#6B7280";
export const LINE = "#E5E7EB";
export const DASH = "#C9CDD2";
export const MINT_BG = "#E1F5EE";
export const MINT_TEXT = "#0B7377";
export const HEADER_INK = "#12262B";

// ── Styles ──────────────────────────────────────────────────────────────
// Letter proportion (816 x 1056), Inter, tabular numerals.
export const styles: Record<string, CSSProperties> = {
  paper: {
    width: 816,
    minHeight: 1056,
    background: PAPER,
    color: INK,
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: 12,
    fontVariantNumeric: "tabular-nums lining-nums",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    margin: "0 auto",
    boxSizing: "border-box",
    position: "relative",
    padding: "0 0 40px",
  },
  bar: { height: 9, background: TEAL },
  content: { padding: "32px 44px 0" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: HEADER_INK,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  columnLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: HEADER_INK,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  headerRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 32,
    marginBottom: 24,
  },
  bold: { fontWeight: 700, color: INK, fontSize: 14 },
  muted: { color: MUTED, fontSize: 11, lineHeight: 1.5 },
  dashed: { borderTop: "1px dashed " + DASH, margin: "24px 0 28px" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    marginBottom: 26,
  },
  th: {
    textAlign: "left",
    padding: "8px 0",
    borderBottom: "1px solid " + LINE,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: MUTED,
    textTransform: "uppercase",
  },
  thRight: {
    textAlign: "right",
    padding: "8px 0",
    borderBottom: "1px solid " + LINE,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: MUTED,
    textTransform: "uppercase",
  },
  td: { padding: "10px 0", color: INK },
  tdRight: { padding: "10px 0", color: INK, textAlign: "right" },
  totalRow: { borderTop: "1px solid " + LINE },
  totalCell: { padding: "10px 0", color: INK, fontWeight: 700 },
  totalCellRight: { padding: "10px 0", color: INK, fontWeight: 700, textAlign: "right" },
  summaryBox: {
    border: "1px solid " + LINE,
    borderRadius: 6,
    overflow: "hidden",
    marginBottom: 20,
  },
  summaryRow: {
    padding: "14px 20px",
    display: "flex",
    justifyContent: "space-between",
    borderBottom: "1px solid " + LINE,
    fontSize: 13,
  },
  summaryRowLast: {
    padding: "18px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: MINT_BG,
  },
  summaryLabel: { color: INK, fontSize: 13 },
  summaryValue: { color: INK, fontWeight: 700, fontSize: 13 },
  netLabel: {
    color: MINT_TEXT,
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  netValue: {
    color: MINT_TEXT,
    fontWeight: 700,
    fontSize: 22,
    fontVariantNumeric: "tabular-nums lining-nums",
  },
  footer: {
    fontSize: 11,
    color: MUTED,
    textAlign: "right",
    marginTop: 12,
  },
  memoBox: {
    marginTop: 24,
    padding: "12px 16px",
    background: "#F9FAFB",
    border: "1px solid " + LINE,
    borderRadius: 6,
  },
  memoLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: MUTED,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  memoText: { fontSize: 12, color: INK, fontStyle: "italic" },
};